import {AuthToken, CreateUserRequest, LoginUserRequest, Meter, Notification, OtpRequest, OtpVerifyRequest, User, SupportRequest, Payment, Request } from '../types/api';
import {
  registerUser,
  loginUser,
  vendMeter,
  resendOtp,
  verifyOtp,
  updateUserProfile,
  fetchUserById,
  getMeterByUser,
  markNotificationAsRead,
  requestMeterbyUser,
  getUserNotifications,
  deleteUserRequest,
  getMeterReadingsByUser,
  forgetPasswordRequest,
  getMeterRequestsByUser,
  getMeterTransactionsByUser,
  fetchDashboardData,
  fetchAvailableMeters,
  getPaymentsByUser,
  sendSupportMessage,
  getUserSupportRequests,
  replySupportMessage,
  createPayment,
  getPaymentGatewayKey,
  requestReset,
  resetPassword,
  fetchGateway,
  sendOtp,
  verifyTokenRecharge,
  fundWalletByUser
} from '../utils/helpers';

export const userService = {

      getDashboardData: async (userId: string) => {
      try {
        const response = await fetchDashboardData({ userId });
        return {
          ...response.data.data,
          status: response.data.status,
          message: response.data.message
        };
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        throw error;
      }
    },

  createUser: async (userData: CreateUserRequest): Promise<User> => {
    try {
      const response = await registerUser(userData);
      if (!response) {
        throw new Error('Failed to create user');
      }
      return response.data.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  },

   loginUser: async (userData: LoginUserRequest): Promise<{ user: User; token: AuthToken }> => {
    try {
      const response = await loginUser(userData);
      return response.data.data;

    } catch (error) {
      console.error('Error logging in user:', error);
      throw error;
    }
  }, // done but comeback

   resendOtp: async (otpData: OtpRequest): Promise<{ status: string, message: string }> => {
     try {
       const response = await resendOtp(otpData);
       return {
         status: response.data.status,
         message: response.data.message ?? ""
       };
     } catch (error) {
       console.error('Error resending OTP:', error);
       throw new Error('Network error occurred');
     }
   },

   verifyOtp: async (otpDataVerify: OtpVerifyRequest): Promise<{ status: string; message: string }> => {
     try {
       const response = await verifyOtp(otpDataVerify);
       return {
         status: response.data.status,
         message: response.data.message ?? ""
       };
     } catch (error) {
       console.error('Error verifying OTP:', error);
       throw new Error('Network error occurred');
     }
   },


  getUserById: async (id: string): Promise<User> => {
    try {
      const response = await fetchUserById({id});
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user by ID:', error);
      throw error;
    }
  },

getMetersByUser: async (id: string): Promise<Meter[]> => {
  try {
    const response = await getMeterByUser({ id });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching user meters:', error);
    throw error;
  }
},

requestMeter: async (data: { userId: string; meterNumbers: string[] }): Promise<{ status: string; message: string, data: any }> => {
  try {
    const response = await requestMeterbyUser(data);
    return {
      status: response.data.status,
      message: response.data.message ?? "",
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error requesting meter:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred');
  }
},


  getNotifications: async (userId: string): Promise<{status: string, message: string, data: Notification[]} | null> => {
    try {
      const response = await getUserNotifications(userId);
      return {
        data: response.data.data,
        status: response.data.status,
        message: response.data.message ?? ""
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },

  

markNotificationAsRead: async (notificationId: string, userId: string): Promise<{
  status: string;
  message: string;
}> => {
  try {
    const response = await markNotificationAsRead(notificationId, userId);
    return {
      status: response.data.status ?? 'error',
      message: response.data.message ?? '',
    };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    throw error;
  }
},

  
vendMeter: async (formData: {userId: string; meterNumber: string; amount: number;
 reference: string;
 transaction_type: string;
 description: string
}): Promise<{ status: string; message: string; data?: any }> => {
  try {
    const response = await vendMeter(formData);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error vending meter:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred during vending');
  }
},


meterReadings: async (userId: string): Promise<{ status: string; message: string; data?: any }> => {
  try {
    const response = await getMeterReadingsByUser(userId);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching meter readings:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while fetching meter readings');
  }
},

forgetPassword: async (email: string): Promise<{ status: string; message: string }> => {
 try {
   const response = await forgetPasswordRequest(email);
   return {
     status: response.data.status,
     message: response.data.message ?? ""
   };
 } catch (error) {
   console.error('Error requesting password reset:', error);
   throw error;
 }
},

deleteUser: async (email: string): Promise<{ status: string; message: string }> => {
 try {
   const response = await deleteUserRequest(email);
   return {
     status: response.data.status,
     message: response.data.message ?? ""
   };
 } catch (error) {
   console.error('Error deleting user:', error);
   throw error;
 }
},

getMeterRequestsByUser: async (userId: string): Promise<Request[]> => {
  try {
    const response = await getMeterRequestsByUser(userId);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching meter requests by user:', error);
    throw error;
  }
},
getMeterTransactionsByUser: async (
  userId: string,
  query?: string
): Promise<{ status: string; message: string; data?: any }> => {
  try {
    const response = await getMeterTransactionsByUser(userId, query);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching meter transactions by user:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error("Network error occurred while fetching meter transactions");
  }
},


fetchAvailableMeters: async (userId: string): Promise<{ status: string; message: string; data?: any }> => {
  try {
    const response = await fetchAvailableMeters({ userId });
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching available meters:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while fetching available meters');
  }
},

getPaymentsByUser: async (userId: string): Promise<{ status: string; message: string; data: Payment[] }> => {
  try {
    const response = await getPaymentsByUser(userId);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching user payments:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while fetching payments');
  }
},

sendSupportMessage: async (supportData: { userId: string; message: string }): Promise<{ status: string; message: string }> => {
  try {
    const response = await sendSupportMessage(supportData);
    return {
      status: response.data.status,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error sending support message:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while sending support message');
  }
},

getUserSupportRequests: async (userId: string): Promise<{ status: string; message: string; data: SupportRequest[] }> => {
  try {
    const response = await getUserSupportRequests(userId);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching support requests:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while fetching support requests');
  }
},

replySupportMessage: async (replyData: { requestId: string; message: string }): Promise<{ status: string; message: string }> => {
  try {
    const response = await replySupportMessage(replyData);
    return {
      status: response.data.status,
      message: response.data.message
    };
  } catch (error: any) {
    console.error('Error replying to support message:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while replying to support message');
  }
},

createPayment: async (paymentData: { amount: number; gateway: string }): Promise<{ status: string; message: string; data?: any }> => {
  try {
    const response = await createPayment(paymentData);
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error creating payment:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while creating payment');
  }
},

getPaymentGatewayKey: async (): Promise<{ status: string; message: string; data: string }> => {
  try {
    const response = await getPaymentGatewayKey();
    return {
      status: response.data.status,
      message: response.data.message,
      data: response.data.data
    };
  } catch (error: any) {
    console.error('Error fetching payment gateway key:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Network error occurred while fetching payment gateway key');
  }
},

updateProfile: async (userData: any) => {
  try {
    const response = await updateUserProfile(userData);
    if (!response || !response.data) {
      throw new Error('Failed to update user profile');
    }
    return response.data.data || response.data;
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
},

  // // DELETE request
  // deleteUser: async (id: string): Promise<void> => {
  //   try {
  //     await deleteUserById({id});
  //   } catch (error) {
  //     console.error('Error deleting user:', error);
  //     throw error;
  //   }
  // },

  requestReset: async (email: string): Promise<{ status: string; message: string }> => {
   try {
     const response = await requestReset(email);
     return {
       status: response.data.status,
       message: response.data.message
     };
   } catch (error: any) {
     console.error('Error requesting password reset:', error);
     if (error.response?.data?.message) {
       throw new Error(error.response.data.message);
     }
     throw new Error('Network error occurred while requesting reset');
   }
  },

  resetPassword: async (data: { token: string; newPassword: string }): Promise<{ status: string; message: string }> => {
    try {
      const response = await resetPassword(data);
      return {
        status: response.data.status,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error occurred while resetting password');
    }
  },

  fetchGateway: async (): Promise<{ status: string; message: string; data: any }> => {
    try {
      const response = await fetchGateway();
      return {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error fetching gateway:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error occurred while fetching gateway');
    }
  },

  sendOtp: async (data: { phone?: string; email?: string }): Promise<{ status: string; message: string }> => {
    try {
      const response = await sendOtp(data);
      return {
        status: response.data.status,
        message: response.data.message
      };
    } catch (error: any) {
      console.error('Error sending OTP:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error occurred while sending OTP');
    }
  },

  verifyTokenRecharge: async (data: { token: string }): Promise<{ status: string; message: string; data?: any }> => {
    try {
      const response = await verifyTokenRecharge(data);
      return {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error verifying token recharge:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error occurred while verifying token recharge');
    }
  },

  fundWalletByUser: async (data: { amount: number }): Promise<{ status: string; message: string; data?: any }> => {
    try {
      const response = await fundWalletByUser(data);
      return {
        status: response.data.status,
        message: response.data.message,
        data: response.data.data
      };
    } catch (error: any) {
      console.error('Error funding wallet by user:', error);
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Network error occurred while funding wallet');
    }
  }
};



